/**
 * A module method that can be used to create a logger and do logging to the client console and to Scalyr.
 * This method serves as a wrapper of the logLevel library and extends it.
 * Reference: https://github.com/pimterry/loglevel
 *
 * Logger includes a total of six levels: trace, debug, info, warn, error, silent.
 * Each level has its corresponding value, from 0(trace) to 5(silent), and the corresponding methods: trace(), debug(), info(), etc.
 * When a level is set, the logging calls below such level are ignored and only the method calls above such level can take effect.
 * For example, when the level is set to 'info', calls to info(), warn(), and error() will log things to console but
 * calls to trace() and debug() will not. Silent is not a method.
 *
 * Default level is TRACE in localhost and INFO otherwise, but can overridden through a query-string param 'logLevel', e.g. logLevel=info.
 * Whether to send the message to Scalyr depends on a knob called 'serverLogLevel'
 *
 * Example:
 *
 * import logger from 'common/ScalyrLogger';
 *
 * // this message will be logged to console, and if serverLogLevel is set to debug or below, will be sent to Scalyr as well.
 * logger.debug('This is a debug message');
 *
 * The message to be logged can be a string, an array of strings, or an object that is a hash of key/value pairs.
 */

import { gql } from '@apollo/client';
import client from 'api/apolloClient';
import { getLogger, Logger, LoggingMethod, LogLevel, LogLevelDesc, LogLevelNumbers } from 'loglevel';
import isString from 'lodash/isString';
import isArray from 'lodash/isArray';
import isPlainObject from 'lodash/isPlainObject';
import StackTrace from 'stacktrace-js';

import * as K from 'common/constants';
import { AccountData, LogType } from 'common/graphqlTypes';
import { skipCircular } from 'common/util/json';
import { generateRandomHash } from 'common/util/generator';
import { getUrlParams } from 'common/util/location';
import { XSRF_NOT_LOGGED_IN } from 'components/auth/constants';

type LogInput = {
  level: keyof LogLevel;
  message: string;
  type: string;
};

type LogOptions = {
  batchSize: number;
  serverLogLevel: keyof LogLevel;
};

export const TRACE = 'TRACE';
export const DEBUG = 'DEBUG';
export const INFO = 'INFO';
export const WARN = 'WARN';
export const ERROR = 'ERROR';
export const SILENT = 'SILENT';
const LOG_LEVELS = [TRACE, DEBUG, INFO, WARN, ERROR, SILENT];

const LOG_TO_SCALYR_QUERY = gql`
  mutation LogToScalyr($logToScalyrInputs: [LogToScalyrInput]) {
    logToScalyr(logToScalyrInputs: $logToScalyrInputs)
  }
`;

let logQueue: Array<LogInput> = [];
const defaultOptions: LogOptions = {
  batchSize: 30,
  serverLogLevel: INFO, // TODO get this from knob
};
const LOG_TIMEOUT_MS = 30_000;
let timeoutTimer: ReturnType<typeof setTimeout>;

const currentUser: { account?: AccountData } = {};
export const setCurrentUserAccount = (account: AccountData) => {
  currentUser.account = account;
};

const clientSession = generateRandomHash();

/**
 * Unload the logQueue and send the logs to Scalyr.
 */
function sendToScalyr() {
  // a helper function to attempt the mutation. For now, if response.logToScalyr is false, re-attempt for rate limit.
  function sendToScalyrMutation(logToScalyrInputs: Array<LogInput>, attempts: number) {
    if (attempts === 0) {
      console.error('Aborted logToScalyr attempts');
    } else if (window.xsrfToken !== XSRF_NOT_LOGGED_IN) {
      // do not make logToScalyr call if user is not logged in
      client
        .mutate({
          mutation: LOG_TO_SCALYR_QUERY,
          variables: { logToScalyrInputs },
        })
        .then((response) => {
          if (response.data && response.data.logToScalyr === false) {
            // mutation didn't complete or hit rate limit, try again
            sendToScalyrMutation(logToScalyrInputs, attempts - 1);
          }
        })
        .catch((error) => {
          // if there is a data error such as non-valid json, just log the message and don't try again.

          console.error(error.message);
          if (error.message.includes('is not allowed for unauthenticated users')) {
            window.xsrfToken = XSRF_NOT_LOGGED_IN;
          }
        });
    }
  }

  if (logQueue.length > 0) {
    sendToScalyrMutation(logQueue.slice(), 2);
    logQueue.length = 0;
  }
}

const settleNextLogging = () => {
  timeoutTimer = setTimeout(sendToScalyr, LOG_TIMEOUT_MS);
};

export const cancelNextLogging = () => {
  if (timeoutTimer) {
    clearTimeout(timeoutTimer);
  }
};

export function addToLogQueue(
  message: unknown,
  level: keyof LogLevel = INFO,
  userConfigOptions: Partial<LogOptions> = defaultOptions,
) {
  const options: LogOptions = { ...defaultOptions, ...userConfigOptions };

  // log to server
  if (isPlainObject(message)) {
    const { id, email, activeTeamV2, activeTeamId } = currentUser.account || {};
    const activeTeam = activeTeamV2?.emailAddress;

    const { userAgent, webdriver } = window.navigator;

    const { pathname, search } = window.location;

    const { buildHash, buildDate } = window.BUILD_INFO || {};

    const { referrer } = document;

    const urlParams = getUrlParams(search);

    const common = {
      id,
      email,
      activeTeam,
      activeTeamId,
      clientSession,
      userAgent,
      webdriver,
      pathname,
      urlParams,
      referrer,
      buildHash,
      buildDate,
    };

    logQueue.push({
      level,
      message: JSON.stringify({ ...common, ...(message as object) }, skipCircular()),
      type: LogType.JSON,
    });
  } else if (isString(message)) {
    logQueue.push({ level, message, type: LogType.STRING });
  } else if (isArray(message)) {
    logQueue.push({
      level,
      message: message.map((item) => JSON.stringify(item, skipCircular())).join(' '),
      type: LogType.STRING,
    });
  } else {
    // since the logger is internally used only, it assumes message is a valid JSON if not string or array
    logQueue.push({ level, message: JSON.stringify(message, skipCircular()), type: LogType.JSON });
  }

  cancelNextLogging();
  if (logQueue.length >= options.batchSize) {
    sendToScalyr();
  } else {
    settleNextLogging();
  }
}

/**
 * Provided by loglevel library, it exposes the window console methods and allows us
 * to send logs before logging to console.
 *
 * @param logger      A logger instance.
 * @param options     Remote options.
 */
const applyRemote = (logger: Logger, options: LogOptions) => {
  /* eslint no-param-reassign: 0 */

  const originalFactory = logger.methodFactory;
  logger.methodFactory = (
    methodName: string,
    logLevel: LogLevelNumbers,
    loggerName: string | symbol,
  ): LoggingMethod => {
    const rawMethod = originalFactory(methodName, logLevel, loggerName);

    return (message, ...extra) => {
      const level = methodName.toUpperCase() as keyof LogLevel;
      if (logger.levels[level] >= logger.levels[options.serverLogLevel]) {
        addToLogQueue(message, level, options);
      }

      // log to client
      rawMethod(message, ...extra);
    };
  };
  /* eslint no-param-reassign: 2 */
};

/**
 * Returns the default log level by hostname.
 * Note: Export this for test purposes.
 *
 * @param hostname    The hostname, e.g. 'localhost'
 * @returns           A log level
 */
export function getDefaultLogLevel(hostname: string): LogLevelDesc {
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return TRACE;
  }
  return INFO;
}

/**
 * Returns a log level from query params or null.
 * Note: Export this for test purposes.
 *
 * @param search    Query params, e.g. '?logLevel=info'
 * @returns         A log level or null if there is no valid log level in the query params
 */
export function getLogLevelFromParams(search: string) {
  const paramValue = new URLSearchParams(search).get('logLevel');
  if (paramValue && LOG_LEVELS.includes(paramValue.toUpperCase())) {
    return paramValue as LogLevelDesc;
  }
  return null;
}

const startOrStopLoggingDependingOnPageVisibility = () => {
  if (document.visibilityState === 'hidden') {
    sendToScalyr();
    cancelNextLogging();
  } else if (document.visibilityState === 'visible') {
    settleNextLogging();
  }
};

const stopLogging = () => {
  cancelNextLogging();
  window.removeEventListener('visibilitychange', startOrStopLoggingDependingOnPageVisibility);
};

const sendLogsAndStopLogging = () => {
  sendToScalyr();
  stopLogging();
};

/**
 * Serves as an init function for ScalyrLogger.
 * Note: Exported for testing. Should not otherwise be used outside of the ScalyrLogger module
 *
 * @param userConfigOptions   User configured LogOptions to replace the default options
 * @returns                   A Logger instance
 */
export const scalyrLogger = (userConfigOptions: Partial<LogOptions> = defaultOptions): Logger => {
  const logger = getLogger(K.SCALYR_LOGGER);
  applyRemote(logger, { ...defaultOptions, ...userConfigOptions });

  // According to specification, the "visibilitychange" event handles both tabs switching and document unloading.
  // More info here: https://www.w3.org/TR/page-visibility/#dfn-visibilitychange.
  window.addEventListener('visibilitychange', startOrStopLoggingDependingOnPageVisibility);

  const defaultLevel = getDefaultLogLevel(window.location.hostname);
  const paramLevel = getLogLevelFromParams(window.location.search);
  const level = paramLevel !== null ? paramLevel : defaultLevel;
  logger.setLevel(level);

  return logger;
};

// TODO: It might be a good idea to aggregate ScalyrLogger public interface in this hook.
export const useScalyrLogger = () => ({
  sendLogsAndStopLogging,
});

/**
 * FRONT-5713 Log unhandled errors and promise rejections
 */
export function initUnhandledRejectionObserver() {
  window.addEventListener('error', (event: ErrorEvent) => {
    const { message, filename, lineno, colno, error } = event;
    StackTrace.fromError(error || new Error()).then((stackframes) => {
      addToLogQueue(
        {
          _type: 'WebappError',
          message,
          filename,
          lineno,
          colno,
          stackframes,
        },
        'ERROR',
        { batchSize: 1 },
      );
    });
  });

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    const { reason } = event;
    addToLogQueue({ _type: 'WebappRejection', reason }, 'ERROR', { batchSize: 1 });
  });
}

/**
 * Exports an instance of ScalyrLogger so that it will be used as a singleton across components.
 */
export default scalyrLogger();
